package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.Edges
import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.layout.engine.LayoutStyle
import top.wcpe.allincore.layout.engine.PositionMode
import top.wcpe.allincore.ui.runtime.element.AutoScroll
import top.wcpe.allincore.ui.runtime.element.Box
import top.wcpe.allincore.ui.runtime.element.CarouselView
import top.wcpe.allincore.ui.runtime.element.CollapsingHeader
import top.wcpe.allincore.ui.runtime.element.DragReorder
import top.wcpe.allincore.ui.runtime.element.InfiniteScroll
import top.wcpe.allincore.ui.runtime.element.MultiColumnVirtualList
import top.wcpe.allincore.ui.runtime.element.PageView
import top.wcpe.allincore.ui.runtime.element.PullToRefresh
import top.wcpe.allincore.ui.runtime.element.ScrollDirection
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.VirtualGrid
import top.wcpe.allincore.ui.runtime.element.VirtualList
import top.wcpe.allincore.ui.runtime.element.VirtualMasonry
import top.wcpe.allincore.ui.runtime.element.VirtualTree
import top.wcpe.allincore.ui.runtime.element.Zoomable
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion

/**
 * 布局基础属性贡献者：width / height / gap / padding / margin / min* / max* / align / justify / flex* +
 * grid / 绝对定位 / 滚动容器族 / 虚拟列表族 / 各装饰器专属属性。
 *
 * 读取器优先返回 [Dimension.Px] 的 double 值；非 px 维度返回 `null`，由调用方回退到 IR defaults。
 * padding / margin / align / justify 仅写不读。
 */
@Suppress("TooManyFunctions", "MagicNumber")
internal object LayoutPropContributor {

    @Suppress("LongMethod", "CyclomaticComplexMethod")
    fun contribute(m: MutableMap<String, PropBinding>) {
        // ---------- 尺寸（读写 Dimension） ----------
        m.dimensionRW("width", { el, d -> el.width(d) }, { it.width })
        m.dimensionRW("height", { el, d -> el.height(d) }, { it.height })
        m.dimensionRW("minWidth", { el, d -> el.style().layout().minWidth = d }, { it.minWidth })
        m.dimensionRW("minHeight", { el, d -> el.style().layout().minHeight = d }, { it.minHeight })
        m.dimensionRW("maxWidth", { el, d -> el.style().layout().maxWidth = d }, { it.maxWidth })
        m.dimensionRW("maxHeight", { el, d -> el.style().layout().maxHeight = d }, { it.maxHeight })
        m.dimensionRW("flexBasis", { el, d -> el.style().layout().flexBasis = d }, { it.flexBasis })
        m.dimensionRW("x", { el, d -> el.x(d) }, { it.x })
        m.dimensionRW("y", { el, d -> el.y(d) }, { it.y })

        // gap：写入无 null 守卫（toFloat 自处理 null），与 flex* 略异，故单列。
        m["gap"] = PropBinding.of(
            { el, v -> el.gap(ValueCoercion.toFloat(v)) },
            { el -> el.layoutStyle().gap.toDouble() },
        )
        m.floatRW("flexGrow", { ls, f -> ls.flexGrow = f }, { it.flexGrow })
        m.floatRW("flexShrink", { ls, f -> ls.flexShrink = f }, { it.flexShrink })

        // ---------- padding / margin（四向 + 单边） ----------
        m.anyProp("padding") { el, v -> el.padding(Edges.all(ValueCoercion.toFloat(v))) }
        // 单边写入仅改对应方向，保留其他三向（依属性出现顺序逐个执行，后写覆盖前写）。
        m.anyProp("paddingTop") { el, v ->
            val e = el.layoutStyle().padding
            el.padding(Edges(ValueCoercion.toFloat(v), e.right(), e.bottom(), e.left()))
        }
        m.anyProp("paddingRight") { el, v ->
            val e = el.layoutStyle().padding
            el.padding(Edges(e.top(), ValueCoercion.toFloat(v), e.bottom(), e.left()))
        }
        m.anyProp("paddingBottom") { el, v ->
            val e = el.layoutStyle().padding
            el.padding(Edges(e.top(), e.right(), ValueCoercion.toFloat(v), e.left()))
        }
        m.anyProp("paddingLeft") { el, v ->
            val e = el.layoutStyle().padding
            el.padding(Edges(e.top(), e.right(), e.bottom(), ValueCoercion.toFloat(v)))
        }
        m.anyProp("margin") { el, v -> el.margin(Edges.all(ValueCoercion.toFloat(v))) }
        m.anyProp("marginTop") { el, v ->
            val e = el.layoutStyle().margin
            el.margin(Edges(ValueCoercion.toFloat(v), e.right(), e.bottom(), e.left()))
        }
        m.anyProp("marginRight") { el, v ->
            val e = el.layoutStyle().margin
            el.margin(Edges(e.top(), ValueCoercion.toFloat(v), e.bottom(), e.left()))
        }
        m.anyProp("marginBottom") { el, v ->
            val e = el.layoutStyle().margin
            el.margin(Edges(e.top(), e.right(), ValueCoercion.toFloat(v), e.left()))
        }
        m.anyProp("marginLeft") { el, v ->
            val e = el.layoutStyle().margin
            el.margin(Edges(e.top(), e.right(), e.bottom(), ValueCoercion.toFloat(v)))
        }

        // ---------- flex 对齐 / 换行 ----------
        m.anyProp("align") { el, v -> el.style().layout().align = ValueCoercion.toAlign(v) }
        m.anyProp("justify") { el, v -> el.style().layout().justify = ValueCoercion.toJustify(v) }
        m.anyProp("wrap") { el, v -> el.style().layout().flexWrap = ValueCoercion.toFlexWrap(v) }

        // ---------- columns / rows：虚拟网格列/行数；其余落 CSS-grid 并切 mode=GRID ----------
        m["columns"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val c = ValueCoercion.toFloat(v).toInt()
            when (el) {
                is VirtualGrid -> el.columns(c)
                is VirtualMasonry -> el.columns(c)
                else -> el.style().layout().let {
                    it.gridColumns = c
                    it.mode = LayoutMode.GRID
                }
            }
        }
        m["rows"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val r = ValueCoercion.toFloat(v).toInt()
            if (el is VirtualGrid) el.rows(r) else el.style().layout().gridRows = r
        }

        // ---------- CSS Grid 进阶（track-list 字符串透传，由 layout-engine GridTrackParser 翻译期解析） ----------
        m.anyProp("gridTemplateColumns") { el, v ->
            el.style().layout().let {
                it.gridTemplateColumns = v.toString()
                it.mode = LayoutMode.GRID
            }
        }
        m.anyProp("gridTemplateRows") { el, v ->
            el.style().layout().let {
                it.gridTemplateRows = v.toString()
                it.mode = LayoutMode.GRID
            }
        }
        m.anyProp("gridColumn") { el, v -> el.style().layout().gridColumn = v.toString() }
        m.anyProp("gridRow") { el, v -> el.style().layout().gridRow = v.toString() }
        m.anyProp("gridAutoFlow") { el, v -> el.style().layout().gridAutoFlow = v.toString() }
        m.anyProp("justifyItems") { el, v -> el.style().layout().justifyItems = ValueCoercion.toAlign(v) }
        // aspectRatio：宽高比约束（数字或 "A/B"），NaN 表示不约束（Taffy 默认，零回归）。
        m.anyProp("aspectRatio") { el, v -> el.style().layout().aspectRatio = ValueCoercion.toAspectRatio(v) }

        // ---------- 绝对定位：layout="absolute" 切 positionMode；Box 另切 mode=ABSOLUTE ----------
        m.anyProp("layout") { el, v ->
            val s = v as? String ?: error("layout 需要字符串: $v")
            // "absolute" → 绝对定位；其余（含 "flex" / 未识别）回 RELATIVE。
            // Box 默认 mode=ROW 无固有方向语义，layout="absolute" 时须同步把 mode 切 ABSOLUTE 脱离 flex 流，
            // 否则子元素 x/y 被 ROW 算法吃掉只剩 height 生效；Column/Row/Grid 有固有方向语义仅自身绝对定位、不动 mode。
            // 不复用 STACK（其会把未显式 x/y 的子补成 (0,0)），独立用 ABSOLUTE 让 IrInterpreter cascade 纯依 mode 判定。
            if (s.trim().lowercase() == "absolute") {
                el.positionMode(PositionMode.ABSOLUTE)
                if (el is Box) {
                    el.style().layout().mode = LayoutMode.ABSOLUTE
                }
            } else {
                el.positionMode(PositionMode.RELATIVE)
            }
        }

        // ---------- 滚动容器族（ScrollableContainer<*>） ----------
        m.scroll("direction") { sc, v -> sc.direction(parseScrollDirection(v.toString())) }
        m.scroll("scrollbarMode") { sc, v -> sc.scrollbarMode(parseScrollbarMode(v.toString())) }
        m.scroll("scrollbarColor") { sc, v -> sc.scrollbarColor(ValueCoercion.toIntColor(v)) }
        m.scroll("scrollbarTrackColor") { sc, v -> sc.scrollbarTrackColor(ValueCoercion.toIntColor(v)) }
        m.scroll("scrollbarWidth") { sc, v -> sc.scrollbarWidth(ValueCoercion.toFloat(v)) }
        m.scroll("bounce") { sc, v -> sc.bounce(ValueCoercion.toBool(v)) }
        m.scroll("nestedScroll") { sc, v -> sc.nestedScroll(ValueCoercion.toBool(v)) }
        m.scroll("bounceMaxDistance") { sc, v -> sc.bounceMaxDistance(ValueCoercion.toFloat(v)) }
        m.scroll("inertia") { sc, v -> sc.inertia(ValueCoercion.toBool(v)) }
        m.scroll("inertiaDecay") { sc, v -> sc.inertiaDecay(ValueCoercion.toFloat(v)) }
        m.scroll("smoothScroll") { sc, v -> sc.smoothScroll(ValueCoercion.toBool(v)) }
        m.scroll("smoothScrollDurationMs") { sc, v -> sc.smoothScrollDurationMs(ValueCoercion.toFloat(v).toInt()) }
        // 滚轮步长：null = 回退默认 20px（须把 null 透给 setter，故不走 scroll 的非空守卫）。
        m["mouseWheelStep"] = PropBinding.writeOnly { el, v ->
            if (el is ScrollableContainer<*>) el.mouseWheelStep(v?.let { ValueCoercion.toFloat(it) })
        }

        // ---------- 虚拟列表族（多类型分发） ----------
        m["itemHeight"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val h = ValueCoercion.toFloat(v)
            when (el) {
                is VirtualList -> el.itemHeight(h)
                is VirtualGrid -> el.itemHeight(h)
                is VirtualTree -> el.itemHeight(h)
                is MultiColumnVirtualList -> el.itemHeight(h)
            }
        }
        m["itemCount"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val c = ValueCoercion.toFloat(v).toInt()
            when (el) {
                is VirtualList -> el.itemCount(c)
                is VirtualGrid -> el.itemCount(c)
                is MultiColumnVirtualList -> el.itemCount(c)
            }
        }
        m["buffer"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val n = ValueCoercion.toFloat(v)
            when (el) {
                is VirtualList -> el.buffer(n.toInt())
                is VirtualGrid -> el.buffer(n.toInt())
                is VirtualTree -> el.buffer(n.toInt())
                is MultiColumnVirtualList -> el.buffer(n.toInt())
                is VirtualMasonry -> el.buffer(n)
            }
        }
        m["itemWidth"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val w = ValueCoercion.toFloat(v)
            when (el) {
                is VirtualGrid -> el.itemWidth(w)
                is VirtualMasonry -> el.itemWidth(w)
            }
        }
        m["gapX"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val g = ValueCoercion.toFloat(v)
            when (el) {
                is VirtualGrid -> el.gapX(g)
                is VirtualMasonry -> el.gapX(g)
            }
        }
        m["gapY"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val g = ValueCoercion.toFloat(v)
            when (el) {
                is VirtualGrid -> el.gapY(g)
                is VirtualMasonry -> el.gapY(g)
            }
        }

        // 各虚拟容器专属
        m.typed<VirtualGrid>("flow") { vg, v -> vg.flow(parseGridFlow(v.toString())) }
        m.typed<VirtualMasonry>("itemHeights") { vm, v -> vm.itemHeights(toFloatList(v)) }
        m.typed<VirtualTree>("indentPerDepth") { vt, v -> vt.indentPerDepth(ValueCoercion.toFloat(v)) }
        m.typed<MultiColumnVirtualList>("headerHeight") { mc, v -> mc.headerHeight(ValueCoercion.toFloat(v)) }
        m.typed<MultiColumnVirtualList>("headerSticky") { mc, v -> mc.headerSticky(ValueCoercion.toBool(v)) }
        m.typed<MultiColumnVirtualList>("columnDefs") { mc, v -> mc.columnDefs(toColumnDefs(v)) }

        // ---------- PageView / CarouselView ----------
        m.typed<PageView>("currentPage") { pv, v -> pv.setCurrentPage(ValueCoercion.toFloat(v).toInt()) }
        m.typed<PageView>("showIndicator") { pv, v -> pv.showIndicator(ValueCoercion.toBool(v)) }
        m.typed<PageView>("indicatorPosition") { pv, v -> pv.indicatorPosition(parseIndicatorPosition(v.toString())) }
        m.typed<CarouselView>("loop") { cv, v -> cv.loop(ValueCoercion.toBool(v)) }
        m.typed<CarouselView>("autoPlay") { cv, v -> cv.autoPlay(ValueCoercion.toBool(v)) }
        m.typed<CarouselView>("autoPlayIntervalMs") { cv, v -> cv.autoPlayIntervalMs(ValueCoercion.toFloat(v).toInt()) }

        // ---------- Phase 3 装饰器：PullToRefresh / InfiniteScroll / CollapsingHeader / Zoomable / AutoScroll / DragReorder ----------
        m.typed<PullToRefresh>("pullIndicatorHeight") { p, v -> p.pullIndicatorHeight(ValueCoercion.toFloat(v)) }
        m.typed<PullToRefresh>("pullText") { p, v -> p.pullText(v.toString()) }
        m.typed<PullToRefresh>("releaseText") { p, v -> p.releaseText(v.toString()) }
        m.typed<PullToRefresh>("refreshingText") { p, v -> p.refreshingText(v.toString()) }
        m.typed<InfiniteScroll>("hasMore") { inf, v -> inf.hasMore(ValueCoercion.toBool(v)) }
        m.typed<InfiniteScroll>("loadingText") { inf, v -> inf.loadingText(v.toString()) }
        m.typed<InfiniteScroll>("noMoreText") { inf, v -> inf.noMoreText(v.toString()) }
        // threshold：PullToRefresh / InfiniteScroll 共用
        m["threshold"] = PropBinding.writeOnly { el, v ->
            if (v == null) return@writeOnly
            val t = ValueCoercion.toFloat(v)
            when (el) {
                is PullToRefresh -> el.threshold(t)
                is InfiniteScroll -> el.threshold(t)
            }
        }
        m.typed<CollapsingHeader>("expandedHeight") { c, v -> c.expandedHeight(ValueCoercion.toFloat(v)) }
        m.typed<CollapsingHeader>("collapsedHeight") { c, v -> c.collapsedHeight(ValueCoercion.toFloat(v)) }
        m.typed<CollapsingHeader>("pinned") { c, v -> c.pinned(ValueCoercion.toBool(v)) }
        m.typed<Zoomable>("zoom") { z, v -> z.zoom(ValueCoercion.toFloat(v)) }
        m.typed<Zoomable>("minZoom") { z, v -> z.minZoom(ValueCoercion.toFloat(v)) }
        m.typed<Zoomable>("maxZoom") { z, v -> z.maxZoom(ValueCoercion.toFloat(v)) }
        m.typed<Zoomable>("zoomStep") { z, v -> z.zoomStep(ValueCoercion.toFloat(v)) }
        m.typed<Zoomable>("zoomFocus") { z, v ->
            z.zoomFocus(
                if (v.toString().equals("mouse", ignoreCase = true)) {
                    Zoomable.ZoomFocus.MOUSE
                } else {
                    Zoomable.ZoomFocus.CENTER
                },
            )
        }
        m.typed<AutoScroll>("speed") { a, v -> a.speed(ValueCoercion.toFloat(v)) }
        m.typed<AutoScroll>("paused") { a, v -> a.paused(ValueCoercion.toBool(v)) }
        m.typed<AutoScroll>("pauseOnHover") { a, v -> a.pauseOnHover(ValueCoercion.toBool(v)) }
        m.typed<AutoScroll>("loopBack") { a, v -> a.loopBack(ValueCoercion.toBool(v)) }
        m.typed<DragReorder>("longPressMs") { d, v -> d.longPressMs(ValueCoercion.toFloat(v).toInt()) }
        m.typed<DragReorder>("dragHandleId") { d, v -> d.dragHandleId(v.toString()) }

        // ---------- 通用：StickyHeader 标记 + tabIndex（任意元素） ----------
        m.anyProp("sticky") { el, v -> el.sticky(ValueCoercion.toBool(v)) }
        m.anyProp("stickyOffset") { el, v -> el.stickyOffset(ValueCoercion.toFloat(v)) }
        // tabIndex：DSL 显式声明覆盖 Tab 默认遍历顺序（默认 TAB_INDEX_UNSET 沿用 DOM 顺序）。
        m.anyProp("tabIndex") { el, v -> el.tabIndex(ValueCoercion.toFloat(v).toInt()) }
    }

    // ===== 注册助手 =====

    /** 任意元素、跳过 null 的写入绑定。 */
    private inline fun MutableMap<String, PropBinding>.anyProp(
        key: String,
        crossinline body: (UIElement<*>, Any) -> Unit,
    ) {
        this[key] = PropBinding.writeOnly { el, v -> if (v != null) body(el, v) }
    }

    /** 仅作用于元素类型 [T]、跳过 null 与不匹配类型的写入绑定。 */
    private inline fun <reified T> MutableMap<String, PropBinding>.typed(
        key: String,
        crossinline body: (T, Any) -> Unit,
    ) {
        this[key] = PropBinding.writeOnly { el, v -> if (v != null && el is T) body(el, v) }
    }

    /** [ScrollableContainer] 专属写入绑定（星投影类型单列，避免 reified 星投影歧义）。 */
    private inline fun MutableMap<String, PropBinding>.scroll(
        key: String,
        crossinline body: (ScrollableContainer<*>, Any) -> Unit,
    ) {
        this[key] = PropBinding.writeOnly { el, v -> if (v != null && el is ScrollableContainer<*>) body(el, v) }
    }

    /** Dimension 读写绑定：写经 [ValueCoercion.toDimension]，读经 [dimensionValue]（仅 px 返回 double）。 */
    private fun MutableMap<String, PropBinding>.dimensionRW(
        key: String,
        set: (UIElement<*>, Dimension) -> Unit,
        get: (LayoutStyle) -> Dimension?,
    ) {
        this[key] = PropBinding.of(
            { el, v -> if (v != null) set(el, ValueCoercion.toDimension(v)) },
            { el -> dimensionValue(get(el.layoutStyle())) },
        )
    }

    /** Float 读写绑定：写经 [ValueCoercion.toFloat]，读返回 Double。 */
    private fun MutableMap<String, PropBinding>.floatRW(
        key: String,
        set: (LayoutStyle, Float) -> Unit,
        get: (LayoutStyle) -> Float,
    ) {
        this[key] = PropBinding.of(
            { el, v -> if (v != null) set(el.style().layout(), ValueCoercion.toFloat(v)) },
            { el -> get(el.layoutStyle()).toDouble() },
        )
    }

    // ===== 解析助手 =====

    /** 解析滚动方向（vertical/horizontal/both，大小写不敏感）。 */
    private fun parseScrollDirection(s: String?): ScrollDirection = when (s?.trim()?.lowercase()) {
        "horizontal" -> ScrollDirection.HORIZONTAL
        "both" -> ScrollDirection.BOTH
        else -> ScrollDirection.VERTICAL
    }

    /** 解析滚动条显示模式（auto/always/never，大小写不敏感）。 */
    private fun parseScrollbarMode(s: String?): ScrollableContainer.ScrollbarMode = when (s?.trim()?.lowercase()) {
        "always" -> ScrollableContainer.ScrollbarMode.ALWAYS
        "never" -> ScrollableContainer.ScrollbarMode.NEVER
        else -> ScrollableContainer.ScrollbarMode.AUTO
    }

    /** 解析网格填充顺序（row-major / column-major）。 */
    private fun parseGridFlow(s: String?): VirtualGrid.Flow =
        if (s?.trim()?.lowercase()?.replace('_', '-') == "column-major") {
            VirtualGrid.Flow.COLUMN_MAJOR
        } else {
            VirtualGrid.Flow.ROW_MAJOR
        }

    /** 解析分页指示器位置（bottom/top/left/right）。 */
    private fun parseIndicatorPosition(s: String?): PageView.IndicatorPosition = when (s?.trim()?.lowercase()) {
        "top" -> PageView.IndicatorPosition.TOP
        "left" -> PageView.IndicatorPosition.LEFT
        "right" -> PageView.IndicatorPosition.RIGHT
        else -> PageView.IndicatorPosition.BOTTOM
    }

    private fun parseColumnAlign(s: String?): MultiColumnVirtualList.ColumnAlign = when (s?.trim()?.lowercase()) {
        "center" -> MultiColumnVirtualList.ColumnAlign.CENTER
        "end" -> MultiColumnVirtualList.ColumnAlign.END
        else -> MultiColumnVirtualList.ColumnAlign.START
    }

    // ===== 值转换 =====

    /** List 值转 List&lt;Float&gt;（瀑布流项高度）；非 Number 元素跳过。 */
    private fun toFloatList(v: Any?): List<Float> =
        (v as? List<*>)?.mapNotNull { (it as? Number)?.toFloat() } ?: emptyList()

    /** List&lt;Map&gt; 值转 List&lt;ColumnDef&gt;，每项支持 key/title/width/align。 */
    private fun toColumnDefs(v: Any?): List<MultiColumnVirtualList.ColumnDef> {
        val list = v as? List<*> ?: return emptyList()
        return list.mapNotNull { o ->
            val map = o as? Map<*, *> ?: return@mapNotNull null
            val key = stringOf(map["key"], "")
            val title = stringOf(map["title"], key)
            val width = (map["width"] as? Number)?.toFloat() ?: 40f
            val align = parseColumnAlign(stringOf(map["align"], "start"))
            MultiColumnVirtualList.ColumnDef(key, title, width, align)
        }
    }

    private fun stringOf(o: Any?, def: String): String = o?.toString() ?: def

    /** 仅 px 维度返回 double，其余返回 null（供调用方回退 IR defaults）。 */
    private fun dimensionValue(dimension: Dimension?): Any? = (dimension as? Dimension.Px)?.value()?.toDouble()
}
